import React, { useState, useEffect } from 'react';
import {
    Upload,
    Maximize2,
    Sparkles,
    ChevronDown,
    CircleSlash,
    Cloud,
    ArrowRight,
    // ArrowUp,
    RotateCw,
    ChevronUp,
    Volume2,
    VolumeX,
    Repeat,
    Play,
    Trash2,
    Loader2
} from 'lucide-react';
import { useUploadAssetMutation } from '../../features/assets/assetsApi';
import { normalizeId } from '../../utils/assetUtils';
import { motion, AnimatePresence } from 'motion/react';
import { createPortal } from 'react-dom';
import { VideoSettings } from '../../types/design';
import Opacity from "../../../imports/Opacity";
import Corner from "../../../imports/Corner";

const ANIMATION_OPTIONS = [
    { id: 'none', label: 'None', icon: CircleSlash, previewClass: '' },
    { id: 'fadeIn', label: 'Fade', icon: Cloud, previewClass: 'preview-animation-fade' },
    { id: 'slideIn', label: 'Slide', icon: ArrowRight, previewClass: 'preview-animation-slideIn' },
    { id: 'zoomIn', label: 'Zoom', icon: Maximize2, previewClass: 'preview-animation-rise' },
    { id: 'rotate', label: 'Rotate', icon: RotateCw, previewClass: 'preview-animation-rotate' },
    // { id: 'parallax', label: 'Parallax', icon: ArrowUp, previewClass: 'preview-animation-slideIn' },
];

interface VideoPropertiesPanelProps {
    selectedVideoId: string | null;
    settings: VideoSettings;
    dimensions?: { left: number; top: number; width: number; height: number; rotation?: number };
    onSettingsChange: (settings: Partial<VideoSettings>, commit?: boolean) => void;
    onDimensionsChange?: (dimensions: Partial<{ left: number; top: number; width: number; height: number; rotation?: number }>) => void;
    onDimensionsCommit?: () => void;
    onClose: () => void;
    onDelete?: (id: string) => void;
}

const PropertyInput: React.FC<{
    label?: string;
    value: number;
    onChange: (val: number) => void;
    width?: string;
    unit?: string;
    step?: number;
    min?: number;
    onBlur?: () => void;
}> = ({ label, value, onChange, width = "w-9", unit, step = 1, min, onBlur }) => (
    <div className="flex items-center h-7 px-2 rounded-lg bg-gray-50 border border-transparent hover:border-gray-200 focus-within:border-[#30A84B] focus-within:bg-white focus-within:shadow-[0_2px_4px_rgba(0,0,0,0.02)] transition-all group relative">
        {label && (
            <span className="text-[9px] font-black text-gray-400 mr-1.5 select-none uppercase tracking-widest group-focus-within:text-[#30A84B]">
                {label}
            </span>
        )}
        <input
            type="number"
            value={typeof value === 'number' ? (step < 1 ? value : Math.round(value)) : 0}
            onChange={(e) => {
                const val = parseFloat(e.target.value);
                if (!isNaN(val)) onChange(val);
            }}
            onBlur={() => onBlur?.()}
            onKeyDown={(e) => {
                if (e.key === 'Enter') onBlur?.();
            }}
            className={`${width} bg-transparent text-[11px] font-bold text-gray-700 outline-none text-left [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none pl-0.5`}
            step={step}
            min={min}
        />
        {unit && <span className="text-[10px] text-gray-400 font-bold ml-0.5">{unit}</span>}
        <div className="flex flex-col ml-1 opacity-0 group-hover:opacity-100 transition-all duration-200">
            <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onChange(value + step); }}
                className="hover:bg-gray-200 rounded-sm p-0.5 text-gray-400 hover:text-gray-900 transition-colors"
            >
                <ChevronUp className="w-2.5 h-2.5" />
            </button>
            <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onChange(min !== undefined ? Math.max(min, value - step) : value - step); }}
                className="hover:bg-gray-200 rounded-sm p-0.5 text-gray-400 hover:text-gray-900 transition-colors"
            >
                <ChevronDown className="w-2.5 h-2.5" />
            </button>
        </div>
    </div>
);

export const VideoPropertiesPanel: React.FC<VideoPropertiesPanelProps> = ({
    selectedVideoId,
    settings,
    dimensions,
    onSettingsChange,
    onDimensionsChange,
    onDimensionsCommit,
    onDelete
}) => {
    const [localSettings, setLocalSettings] = useState(settings);
    const [uploadAsset, { isLoading: isUploading }] = useUploadAssetMutation();
    const [isAnimDropdownOpen, setIsAnimDropdownOpen] = useState(false);
    const [animDropdownPos, setAnimDropdownPos] = useState({ top: 0, left: 0 });
    const [hoveredAnim, setHoveredAnim] = useState<string | null>(null);
    const animTriggerRef = React.useRef<HTMLButtonElement>(null);

    useEffect(() => {
        setLocalSettings(settings);
    }, [settings]);

    React.useLayoutEffect(() => {
        if (isAnimDropdownOpen && animTriggerRef.current) {
            const updatePosition = () => {
                if (animTriggerRef.current) {
                    const rect = animTriggerRef.current.getBoundingClientRect();
                    setAnimDropdownPos({
                        top: rect.bottom + 8,
                        left: Math.max(16, rect.left - 120)
                    });
                }
            };
            updatePosition();
            window.addEventListener('resize', updatePosition);
            return () => window.removeEventListener('resize', updatePosition);
        }
    }, [isAnimDropdownOpen]);

    useEffect(() => {
        if (!isAnimDropdownOpen) return;
        const handleClickOutside = (e: MouseEvent) => {
            const animPortal = document.getElementById('video-anim-portal');
            const isAnimTrigger = animTriggerRef.current?.contains(e.target as Node);

            if (animPortal && !animPortal.contains(e.target as Node) && !isAnimTrigger) {
                setIsAnimDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isAnimDropdownOpen]);

    const updateSetting = (key: keyof VideoSettings, value: any, commit = false) => {
        const newSettings = { ...localSettings, [key]: value };
        setLocalSettings(newSettings);
        onSettingsChange({ [key]: value }, commit);
    };

    const handleVideoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            try {
                const formData = new FormData();
                formData.append('file', file);
                formData.append('name', file.name);

                const response = await uploadAsset(formData).unwrap() as any;
                const videoUrl = response?.data?.url || response?.url;
                const assetId = normalizeId(response?.data?._id || response?._id);

                if (videoUrl) {
                    updateSetting('assetId', assetId);
                    updateSetting('url', videoUrl, true);
                }
            } catch (err) {
                console.error('Failed to upload video:', err);
            }
        }
    };

  if (!selectedVideoId) return null;

  const updateDimension = (key: string, value: number) => {
    if (onDimensionsChange) {
      onDimensionsChange({ [key]: value });
    }
  };

  return (
    <div className="flex flex-col gap-1.5 p-0.5" onMouseDown={(e) => e.stopPropagation()}>
      {/* Row 1: Transform & Actions */}
      <div className="flex items-center h-8 px-1 gap-0.5">
        {/* Label */}
        <div className="px-2 text-[10px] font-black text-[#30A84B] uppercase tracking-widest border-r border-gray-100 mr-1 select-none whitespace-nowrap">
          Video
        </div>

        {dimensions && (
          <div className="flex items-center gap-1.5 px-2 border-r border-gray-100 mr-1" title="Position">
            <div className="flex items-center gap-1">
              <PropertyInput
                label="X"
                value={dimensions?.left ?? 0}
                onChange={(val) => updateDimension('left', val)}
                onBlur={onDimensionsCommit}
              />
              <PropertyInput
                label="Y"
                value={dimensions?.top ?? 0}
                onChange={(val) => updateDimension('top', val)}
                onBlur={onDimensionsCommit}
              />
            </div>
          </div>
        )}

        {/* Size Section */}
        {dimensions && (
          <div className="flex items-center gap-1.5 px-2 border-r border-gray-100 mr-1" title="Size">
            <Maximize2 className="w-5 h-5 text-gray-400" />
            <div className="flex items-center gap-1">
              <PropertyInput
                label="W"
                value={dimensions?.width ?? 0}
                onChange={(val) => updateDimension('width', val)}
                onBlur={onDimensionsCommit}
                width="w-12"
              />
              <PropertyInput
                label="H"
                value={dimensions?.height ?? 0}
                onChange={(val) => updateDimension('height', val)}
                onBlur={onDimensionsCommit}
                width="w-12"
              />
            </div>
          </div>
        )}

        {/* Rotation Section */}
        {dimensions && (
          <div className="flex items-center gap-1.5 px-2 border-r border-gray-100 mr-1" title="Rotation">
            <RotateCw className="w-5 h-5 text-gray-400" />
            <PropertyInput
              label=""
              value={dimensions?.rotation || 0}
              onChange={(val) => updateDimension('rotation', val)}
              onBlur={onDimensionsCommit}
              unit="°"
              width="w-10"
            />
          </div>
        )}

        {/* Upload/Replace */}
        <div className="flex items-center h-7 px-1 border-l border-gray-100 ml-1">
          <input
            type="file"
            accept="video/*"
            onChange={handleVideoUpload}
            className="hidden"
            id="video-toolbar-upload"
          />
          <label
            htmlFor={isUploading ? undefined : "video-toolbar-upload"}
            className={`flex items-center gap-1.5 h-6 px-2.5 rounded-md text-[13px] font-semibold text-gray-700 transition-colors ${isUploading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-100 cursor-pointer'
              }`}
          >
            {isUploading ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Upload className="w-5 h-5" />
            )}
            <span>{isUploading ? 'Uploading...' : 'Replace'}</span>
          </label>
        </div>

        {/* Delete Button */}
        {onDelete && (
          <div className="flex items-center ml-auto pl-1 gap-0.5 border-l border-gray-100">
            <button
              type="button"
              onClick={() => selectedVideoId && onDelete?.(selectedVideoId)}
              className="p-1.5 rounded-md text-gray-400 hover:text-red-500 hover:bg-red-50 transition-all"
              title="Delete Video"
            >
              <Trash2 className="w-5 h-5" />
            </button>
          </div>
        )}
      </div>

      {/* Divider */}
      <div className="h-px bg-gray-100/60 mx-2" />

      {/* Row 2: Styling & Effects */}
      <div className="flex items-center h-9 px-1 gap-0.5">
        {/* Object Fit */}
        <div className="flex items-center gap-0.5 px-1 border-r border-gray-100 mr-1">
          {(['cover', 'contain'] as const).map((fit) => (
            <button
              key={fit}
              type="button"
              onClick={() => updateSetting('objectFit', fit, true)}
              className={`h-7 px-2.5 rounded-md text-[11px] font-bold uppercase transition-all ${localSettings.objectFit === fit
                ? 'bg-green-50 text-green-600'
                : 'text-gray-500 hover:bg-gray-100'
                }`}
            >
              {fit}
            </button>
          ))}
        </div>

        {/* Toggles: Muted, Loop, Autoplay */}
        <div className="flex items-center gap-0.5 px-1 border-r border-gray-100 mr-1">
          <button
            type="button"
            onClick={() => updateSetting('muted', !localSettings.muted, true)}
            className={`p-1.5 rounded-md transition-all ${localSettings.muted
              ? 'bg-green-50 text-green-600'
              : 'text-gray-500 hover:bg-gray-100'
              }`}
            title={localSettings.muted ? "Unmute" : "Mute"}
          >
            {localSettings.muted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
          </button>
          <button
            type="button"
            onClick={() => updateSetting('loop', !localSettings.loop, true)}
            className={`p-1.5 rounded-md transition-all ${localSettings.loop
              ? 'bg-green-50 text-green-600'
              : 'text-gray-500 hover:bg-gray-100'
              }`}
            title="Loop"
          >
            <Repeat className="w-5 h-5" />
          </button>
          <button
            type="button"
            onClick={() => updateSetting('autoplay', !localSettings.autoplay, true)}
            className={`p-1.5 rounded-md transition-all ${localSettings.autoplay
              ? 'bg-green-50 text-green-600'
              : 'text-gray-500 hover:bg-gray-100'
              }`}
            title="Autoplay"
          >
            <Play className="w-5 h-5" />
          </button>
        </div>

        {/* Opacity */}
        <div className="flex items-center gap-2 px-2 border-r border-gray-100 mr-1" title="Opacity">
          <div className="w-5 h-5 text-gray-400">
            <Opacity />
          </div>
          <input
            type="range"
            value={localSettings.opacity ?? 100}
            onChange={(e) => updateSetting('opacity', parseInt(e.target.value), false)}
            onMouseUp={(e) => updateSetting('opacity', parseInt((e.target as HTMLInputElement).value), true)}
            onBlur={(e) => updateSetting('opacity', parseInt((e.target as HTMLInputElement).value), true)}
            min="0"
            max="100"
            className="w-16 h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-green-500 hover:accent-green-600"
          />
          <span className="text-[11px] font-bold text-gray-500 w-7 text-right">
            {Math.round(localSettings.opacity)}%
          </span>
        </div>

        {/* Corner Radius */}
        <div className="flex items-center gap-2 px-2 border-r border-gray-100 mr-1" title="Corner Radius">
          <div className="flex items-center group">
            <div className="w-5 h-5 text-gray-400 group-focus-within:text-[#30A84B] transition-colors mr-1.5">
              <Corner />
            </div>
            <PropertyInput
              value={localSettings.borderRadius ?? 0}
              onChange={(val) => updateSetting('borderRadius', val, true)}
              width="w-7"
              min={0}
            />
          </div>
        </div>

        {/* Animation Selector */}
        <div className="flex items-center gap-2 px-2 border-r border-gray-100 mr-1" title="Video Animation">
          <div className="relative">
            <button
              ref={animTriggerRef}
              type="button"
              onClick={() => setIsAnimDropdownOpen(!isAnimDropdownOpen)}
              className="flex items-center gap-1.5 h-7 px-2 rounded-md hover:bg-gray-100 transition-colors group"
            >
              <Sparkles className={`w-5 h-5 transition-colors ${localSettings.animation?.type && localSettings.animation.type !== 'none' ? 'text-[#30A84B]' : 'text-gray-400 group-hover:text-[#30A84B]'}`} />
              <span className="text-[12px] font-bold text-gray-700 min-w-[40px] text-left uppercase tracking-tighter">
                {ANIMATION_OPTIONS.find(a => a.id === (localSettings.animation?.type || 'none'))?.label || 'Static'}
              </span>
              <ChevronDown className={`w-3 h-3 text-gray-400 transition-transform ${isAnimDropdownOpen ? 'rotate-180' : ''}`} />
            </button>

            {createPortal(
              <AnimatePresence>
                {isAnimDropdownOpen && (
                  <motion.div
                    id="video-anim-portal"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 8 }}
                    className="fixed w-[260px] bg-white border border-gray-200 shadow-[0_12px_40px_rgba(0,0,0,0.12)] rounded-2xl z-[9999] overflow-hidden origin-top-left"
                    style={{ top: `${animDropdownPos.top}px`, left: `${animDropdownPos.left}px`, pointerEvents: 'auto' }}
                    onMouseDown={(e) => e.stopPropagation()}
                  >
                    <div className="px-4 py-3 border-b border-gray-50 flex items-center justify-between bg-gray-50/30">
                      <span className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.15em]">Motion Presets</span>
                    </div>

                    <div className="p-2 grid grid-cols-2 gap-2 max-h-[380px] overflow-y-auto scrollbar-none">
                      {ANIMATION_OPTIONS.map((anim) => (
                        <button
                          key={anim.id}
                          type="button"
                          onClick={() => {
                            updateSetting('animation', { ...localSettings.animation, type: anim.id }, true);
                            setIsAnimDropdownOpen(false);
                          }}
                          onMouseEnter={() => setHoveredAnim(anim.id)}
                          onMouseLeave={() => setHoveredAnim(null)}
                          className={`group relative flex flex-col items-center justify-center gap-2 p-3.5 rounded-xl border transition-all duration-200 ${localSettings.animation?.type === anim.id
                            ? 'border-[#30A84B] bg-[#30A84B]/[0.03] ring-1 ring-[#30A84B]/20'
                            : 'border-gray-100 hover:border-gray-200 hover:bg-gray-50'
                            }`}
                        >
                          {localSettings.animation?.type === anim.id && (
                            <div className="absolute top-2 right-2 w-1.5 h-1.5 rounded-full bg-[#30A84B]" />
                          )}

                          <div className={`flex items-center justify-center w-11 h-11 rounded-xl transition-all duration-300 ${localSettings.animation?.type === anim.id
                            ? 'bg-[#30A84B] text-white shadow-[0_4px_12px_rgba(48,168,75,0.25)]'
                            : 'bg-gray-50 text-gray-400 group-hover:bg-white group-hover:text-gray-600 group-hover:shadow-sm'
                            } ${hoveredAnim === anim.id ? anim.previewClass : ''}`}
                            style={{ '--preview-duration': '0.7s', '--preview-iteration': 'infinite' } as any}
                          >
                            <anim.icon className="w-[22px] h-[22px] stroke-[1.8]" />
                          </div>

                          <span className={`text-[11px] font-bold tracking-tight transition-colors ${localSettings.animation?.type === anim.id ? 'text-[#30A84B]' : 'text-gray-500 group-hover:text-gray-900'
                            }`}>
                            {anim.label}
                          </span>
                        </button>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>,
              document.body
            )}
          </div>

          {localSettings.animation && localSettings.animation.type !== 'none' && (
            <PropertyInput
              value={localSettings.animation.duration || 1000}
              onChange={(val) => updateSetting('animation', { ...localSettings.animation, duration: val }, true)}
              width="w-10"
              unit="MS"
              step={100}
              min={0}
            />
          )}
        </div>
      </div>
    </div>
  );
};
